import React, { useEffect, useState } from 'react'
import { Button, Message } from 'semantic-ui-react'
import { createTask } from 'utility/services/crm'
import { toApiError } from 'utility/crm/api-error'
import { toDateInput } from 'utility/crm/dates'
import { useAuth } from 'context/user'
import { ALL_CRM_SCOPES, invalidateCrm, useCrmLookups, useCrmToast } from 'hooks/crm'
import { CrmModal } from './CrmOverlay'

const label = { display: 'block', fontWeight: 600, margin: '10px 0 4px' }
const field = { width: '100%', padding: 8 }
const tomorrow = () => toDateInput(new Date(Date.now() + 86400000))

/**
 * Add a follow-up task for a contact (or account), optionally repeating monthly or quarterly.
 * @param {{ open: boolean, contact?: import('utility/crm/types').CrmContact|null, accountId?: number|null,
 *   dealId?: number|null, onClose: () => void }} props
 */
export const TaskDialog = ({ open, contact = null, accountId = null, dealId = null, onClose }) => {
    const { user } = useAuth()
    const { lookups } = useCrmLookups()
    const { successToast } = useCrmToast()
    const [title, setTitle] = useState('')
    const [due, setDue] = useState(tomorrow())
    const [owner, setOwner] = useState('')
    const [recur, setRecur] = useState('')
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState(null)
    const [touched, setTouched] = useState(false)

    // Fresh form each time it opens; the owner defaults to the signed-in user when they are in the user list.
    useEffect(() => {
        if (!open) return
        setTitle('')
        setDue(tomorrow())
        setRecur('')
        setError(null)
        setTouched(false)
        const me = (lookups?.Users || []).find(u => u.UserId === user?.unique_id)
        setOwner(me ? me.UserId : '')
    }, [open]) // eslint-disable-line

    const titleMissing = !title.trim()
    const target = contact ? { ContactId: contact.ContactId } : { AccountId: accountId }

    const submit = async event => {
        event?.preventDefault()
        setTouched(true)
        if (titleMissing) return
        setBusy(true)
        setError(null)
        try {
            await createTask({
                Title: title.trim(),
                DueDate: due || undefined,
                ...target,
                DealId: dealId || undefined,
                OwnerUserId: owner === '' ? undefined : Number(owner),
                Recur: recur || null,
            })
            successToast(recur ? `Follow-up added (repeats ${recur})` : 'Follow-up added')
            invalidateCrm(...ALL_CRM_SCOPES)
            onClose()
        } catch (err) {
            setError(toApiError(err).message)
        } finally {
            setBusy(false)
        }
    }

    return (
        <CrmModal
            open={open}
            onClose={busy ? () => {} : onClose}
            title="Add follow-up"
            subtitle={contact ? contact.Name : ''}
            width={480}
            footer={
                <>
                    <Button type="button" onClick={onClose} disabled={busy}>
                        Cancel
                    </Button>
                    <Button type="submit" form="crm-task-form" primary loading={busy} disabled={busy}>
                        Add follow-up
                    </Button>
                </>
            }
        >
            <form id="crm-task-form" noValidate onSubmit={submit}>
                {error ? (
                    <Message negative role="alert">
                        {error}
                    </Message>
                ) : null}
                <label htmlFor="task-title" style={label}>
                    What needs to happen? *
                </label>
                <input id="task-title" value={title} onChange={e => setTitle(e.target.value)} style={field} maxLength={200} placeholder="e.g. Send revised pricing" aria-invalid={touched && titleMissing} />
                {touched && titleMissing ? (
                    <p role="alert" style={{ color: '#dc2626', margin: '2px 0', fontSize: '0.8rem' }}>
                        Describe the follow-up
                    </p>
                ) : null}

                <label htmlFor="task-due" style={label}>
                    Due
                </label>
                <input id="task-due" type="date" value={due} onChange={e => setDue(e.target.value)} style={field} />

                <label htmlFor="task-recur" style={label}>
                    Repeat
                </label>
                <select id="task-recur" value={recur} onChange={e => setRecur(e.target.value)} style={field}>
                    <option value="">Does not repeat</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                </select>

                <label htmlFor="task-owner" style={label}>
                    Assigned to
                </label>
                <select id="task-owner" value={owner} onChange={e => setOwner(e.target.value)} style={field}>
                    <option value="">Unassigned</option>
                    {(lookups?.Users || []).map(u => (
                        <option key={u.UserId} value={u.UserId}>
                            {u.FullName}
                        </option>
                    ))}
                </select>
            </form>
        </CrmModal>
    )
}
